'use client';

/**
 * OutcomeBillingTab — operator view of outcome-based pricing.
 *
 * For one organization:
 *   • Price schedule — what the org pays per outcome (from its plan, or a
 *     negotiated per-org schedule set here)
 *   • Detect now — find new outcomes and move verified ones past their waiting
 *     period to billable (also runs nightly)
 *   • Outcomes — the ledger, with review actions: verify / reject pending ones,
 *     reverse (credit) invoiced ones, and resolve client disputes
 * Billable outcomes are added to the org's next invoice by the usage invoice
 * generator (Usage tab).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Download, Flag, Loader2, Plus, RotateCcw, Save, Search as ScanSearch, Target, Trash2, X } from 'lucide-react';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function headers(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (cboId) h['X-CBO-ID'] = cboId;
  if (tenantSlug) h['X-Tenant-ID'] = tenantSlug;
  return h;
}
async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${path}`, { ...init, headers: { ...headers(), ...(init.headers || {}) } });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
  return data as T;
}

interface Definition { code: string; name: string; verification: string; lag_days: number; unit_label: string }
interface ScheduleEntry { outcome_code: string; unit_price: number; included_per_month: number; monthly_cap: number | null; program_ids?: number[] | null }
interface OutcomeEvent {
  id: number; outcome_code: string; member_id: number | null; occurred_at: string; status: string;
  evidence: Record<string, any>; billable_after: string | null; rejected_reason: string | null;
  disputed: boolean; dispute_note: string | null; unit_price_cents: number | null; invoice_id: number | null;
}

const STATUS_LABEL: Record<string, string> = {
  detected: 'Detected', pending_verification: 'Needs review', verified: 'Verified (waiting period)',
  billable: 'Billable', invoiced: 'Invoiced', rejected: 'Rejected', reversed: 'Reversed',
};
const STATUS_CLS: Record<string, string> = {
  pending_verification: 'bg-amber-50 text-amber-700', verified: 'bg-blue-50 text-blue-700',
  billable: 'bg-indigo-50 text-indigo-700', invoiced: 'bg-green-50 text-green-700',
  rejected: 'bg-gray-100 text-gray-500', reversed: 'bg-gray-100 text-gray-500', detected: 'bg-gray-100 text-gray-700',
};
const money = (cents: number | null) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);

export default function OutcomeBillingTab() {
  const [orgs, setOrgs] = useState<{ id: number; name: string }[]>([]);
  const [cboId, setCboId] = useState<number | null>(null);
  const [defs, setDefs] = useState<Definition[]>([]);
  const [plan, setPlan] = useState<string | null>(null);
  const [planSchedule, setPlanSchedule] = useState<Record<string, ScheduleEntry>>({});
  const [orgEntries, setOrgEntries] = useState<ScheduleEntry[]>([]);
  const [events, setEvents] = useState<OutcomeEvent[]>([]);
  const [counts, setCounts] = useState<{ outcome_code: string; status: string; n: number }[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [reasonFor, setReasonFor] = useState<{ id: number; action: string } | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${PLATFORM_BACKEND_URL}/api/cbos`, { headers: headers() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setOrgs((Array.isArray(d) ? d : d.cbos || []).map((o: any) => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgs([]));
    api<{ definitions: Definition[] }>('/outcomes/definitions').then((d) => setDefs(d.definitions)).catch(() => setDefs([]));
  }, []);

  const defName = useMemo(() => Object.fromEntries(defs.map((d) => [d.code, d.name])), [defs]);

  const load = useCallback(async () => {
    if (!cboId) return;
    setError(null);
    try {
      const [sch, ev] = await Promise.all([
        api<any>(`/outcomes/schedule/${cboId}`),
        api<any>(`/outcomes/events?cbo_id=${cboId}${statusFilter ? `&status=${statusFilter}` : ''}&limit=500`),
      ]);
      setPlan(sch.plan);
      setPlanSchedule(sch.plan_schedule || {});
      setOrgEntries(Object.entries(sch.org_schedule || {}).map(([code, v]: [string, any]) => ({ outcome_code: code, ...v })));
      setEvents(ev.events);
      setCounts(ev.counts);
    } catch (e: any) {
      setError(e.message);
    }
  }, [cboId, statusFilter]);

  useEffect(() => { setNotice(null); load(); }, [load]);

  const act = async (key: string, fn: () => Promise<any>, done: string) => {
    setBusy(key); setError(null);
    try { await fn(); if (done) setNotice(done); await load(); }
    catch (e: any) { setError(e.message); }
    finally { setBusy(null); }
  };

  const detectNow = () =>
    act('detect', async () => {
      const r = await api<any>('/outcomes/detect', { method: 'POST', body: JSON.stringify({ cbo_id: cboId }) });
      const found = Object.values(r.detected || {}).filter((v) => typeof v === 'number').reduce((a: number, b: any) => a + b, 0);
      setNotice(`Found ${found} new outcome(s). ${r.promoted ? `${r.promoted.billable} became billable, ${r.promoted.rejected} rejected.` : ''}`);
    }, '');

  const saveSchedule = () =>
    act('schedule', () => api(`/outcomes/schedule/${cboId}`, {
      method: 'PUT',
      body: JSON.stringify({ entries: orgEntries.filter((e) => e.outcome_code).map((e) => ({ ...e, monthly_cap: e.monthly_cap === null || (e.monthly_cap as any) === '' ? null : Number(e.monthly_cap) })) }),
    }), 'Saved the organization’s outcome prices.');

  const runAction = (ev: OutcomeEvent, action: string, body: any = {}) =>
    act(`${action}:${ev.id}`, () => api(`/outcomes/events/${ev.id}/${action}`, { method: 'POST', body: JSON.stringify(body) }), {
      verify: 'Verified.', reject: 'Rejected.', reverse: 'Reversed.', dispute: 'Marked as disputed; held off invoices.', 'resolve-dispute': 'Dispute resolved.',
    }[action] || 'Done.');

  const download = async () => {
    const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops/outcomes/statement?cbo_id=${cboId}&format=csv`, { headers: headers() });
    if (!r.ok) { setError(`Download failed (${r.status})`); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a'); a.href = url; a.download = `outcomes-${cboId}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const countFor = (status: string) => counts.filter((c) => c.status === status).reduce((a, c) => a + Number(c.n), 0);
  const needsReason = (action: string) => ['reject', 'reverse', 'dispute'].includes(action);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Target className="h-5 w-5" /> Outcome billing</h2>
          <p className="text-sm text-gray-500 max-w-2xl">
            Outcomes the platform records for an organization (enrollments, completed referrals …), what its plan pays for each,
            and the review actions before they are invoiced.
          </p>
        </div>
        <select
          id="outcome-billing-org"
          value={cboId ?? ''}
          onChange={(e) => setCboId(e.target.value ? Number(e.target.value) : null)}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 min-w-[16rem]"
        >
          <option value="">Select an organization…</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}
      {notice && !error && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">{notice}</div>}

      {!cboId ? (
        <p className="text-sm text-gray-500">Select an organization to manage its outcome billing.</p>
      ) : (
        <>
          {/* Schedule */}
          <section className="bg-white dark:bg-gray-800 border rounded-xl p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-medium">Price per outcome {plan && <span className="text-sm font-normal text-gray-500">· plan: {plan}</span>}</h3>
              <div className="flex gap-2">
                <button onClick={() => setOrgEntries((e) => [...e, { outcome_code: '', unit_price: 0, included_per_month: 0, monthly_cap: null }])} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="h-4 w-4" /> Add outcome price
                </button>
                <button onClick={saveSchedule} disabled={!!busy} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400">
                  {busy === 'schedule' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save prices
                </button>
              </div>
            </div>
            {Object.keys(planSchedule).length > 0 && (
              <p className="text-xs text-gray-500">
                Plan prices: {Object.entries(planSchedule).map(([c, v]) => `${defName[c] || c} $${v.unit_price}`).join(' · ')}. Prices set here override the plan for this organization.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="text-xs text-gray-500"><tr>
                  <th className="text-left py-1.5">Outcome</th><th className="text-left py-1.5">Price ($)</th>
                  <th className="text-left py-1.5">Included / month</th><th className="text-left py-1.5">Monthly cap</th><th />
                </tr></thead>
                <tbody>
                  {orgEntries.length === 0 && (
                    <tr><td colSpan={5} className="py-2 text-gray-500">No organization-specific prices{Object.keys(planSchedule).length ? '; the plan’s prices apply.' : '. This organization isn’t billed for outcomes yet.'}</td></tr>
                  )}
                  {orgEntries.map((e, i) => (
                    <tr key={i}>
                      <td className="py-1 pr-2">
                        <select id={`oc-code-${i}`} value={e.outcome_code} onChange={(x) => setOrgEntries((s) => s.map((r, j) => (j === i ? { ...r, outcome_code: x.target.value } : r)))} className="border rounded px-2 py-1 w-full">
                          <option value="">Choose…</option>
                          {defs.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
                        </select>
                      </td>
                      <td className="py-1 pr-2"><input id={`oc-price-${i}`} type="number" min="0" step="0.01" value={e.unit_price} onChange={(x) => setOrgEntries((s) => s.map((r, j) => (j === i ? { ...r, unit_price: Number(x.target.value) } : r)))} className="border rounded px-2 py-1 w-28" /></td>
                      <td className="py-1 pr-2"><input id={`oc-incl-${i}`} type="number" min="0" value={e.included_per_month} onChange={(x) => setOrgEntries((s) => s.map((r, j) => (j === i ? { ...r, included_per_month: Number(x.target.value) } : r)))} className="border rounded px-2 py-1 w-24" /></td>
                      <td className="py-1 pr-2"><input id={`oc-cap-${i}`} type="number" min="0" placeholder="No cap" value={e.monthly_cap ?? ''} onChange={(x) => setOrgEntries((s) => s.map((r, j) => (j === i ? { ...r, monthly_cap: x.target.value === '' ? null : Number(x.target.value) } : r)))} className="border rounded px-2 py-1 w-24" /></td>
                      <td className="py-1 text-right"><button onClick={() => setOrgEntries((s) => s.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600" title="Remove"><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Ledger */}
          <section className="bg-white dark:bg-gray-800 border rounded-xl p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2 text-xs">
                {['pending_verification', 'verified', 'billable', 'invoiced', 'rejected'].map((s) => (
                  <button key={s} onClick={() => setStatusFilter(statusFilter === s ? '' : s)} className={`px-2 py-1 rounded ${STATUS_CLS[s]} ${statusFilter === s ? 'ring-2 ring-indigo-400' : ''}`}>
                    {STATUS_LABEL[s]}: {countFor(s)}
                  </button>
                ))}
                {statusFilter && <button onClick={() => setStatusFilter('')} className="text-gray-500 underline">Show all</button>}
              </div>
              <div className="flex gap-2">
                <button onClick={detectNow} disabled={!!busy} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400">
                  {busy === 'detect' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />} Detect now
                </button>
                <button onClick={download} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"><Download className="h-4 w-4" /> Statement CSV</button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="text-xs text-gray-500 border-b"><tr>
                  <th className="text-left py-2 pr-2">Date</th><th className="text-left py-2 pr-2">Outcome</th><th className="text-left py-2 pr-2">Member</th>
                  <th className="text-left py-2 pr-2">Detail</th><th className="text-left py-2 pr-2">Status</th><th className="text-right py-2 pr-2">Price</th><th className="text-right py-2">Actions</th>
                </tr></thead>
                <tbody className="divide-y">
                  {events.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-gray-500">No outcomes yet. Set prices above, then Detect now.</td></tr>}
                  {events.map((ev) => (
                    <React.Fragment key={ev.id}>
                      <tr>
                        <td className="py-2 pr-2 whitespace-nowrap">{new Date(ev.occurred_at).toLocaleDateString()}</td>
                        <td className="py-2 pr-2">{defName[ev.outcome_code] || ev.outcome_code}</td>
                        <td className="py-2 pr-2">{ev.member_id ?? '—'}</td>
                        <td className="py-2 pr-2 text-gray-500">{ev.evidence?.program || ev.evidence?.service_type || ev.evidence?.referral_number || ''}</td>
                        <td className="py-2 pr-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CLS[ev.status] || ''}`}>{STATUS_LABEL[ev.status] || ev.status}</span>
                          {ev.disputed && <span className="ml-1 px-2 py-0.5 rounded text-xs bg-red-50 text-red-700" title={ev.dispute_note || ''}>Disputed</span>}
                          {ev.status === 'rejected' && ev.rejected_reason && <div className="text-xs text-gray-400 mt-0.5">{ev.rejected_reason}</div>}
                        </td>
                        <td className="py-2 pr-2 text-right tabular-nums">{money(ev.unit_price_cents)}</td>
                        <td className="py-2 text-right text-xs whitespace-nowrap">
                          <div className="inline-flex gap-3">
                            {ev.status === 'pending_verification' && (
                              <button onClick={() => runAction(ev, 'verify')} className="inline-flex items-center gap-1 text-green-700 hover:underline"><Check className="h-3 w-3" /> Verify</button>
                            )}
                            {['detected', 'pending_verification', 'verified', 'billable'].includes(ev.status) && !ev.disputed && (
                              <button onClick={() => { setReasonFor({ id: ev.id, action: 'reject' }); setReason(''); }} className="inline-flex items-center gap-1 text-gray-600 hover:text-red-600"><X className="h-3 w-3" /> Reject</button>
                            )}
                            {ev.status === 'invoiced' && !ev.disputed && (
                              <button onClick={() => { setReasonFor({ id: ev.id, action: 'reverse' }); setReason(''); }} className="inline-flex items-center gap-1 text-gray-600 hover:text-red-600"><RotateCcw className="h-3 w-3" /> Reverse (credit)</button>
                            )}
                            {ev.disputed ? (
                              <>
                                <button onClick={() => runAction(ev, 'resolve-dispute', { uphold: true, note: 'dispute upheld' })} className="text-red-600 hover:underline">Uphold dispute</button>
                                <button onClick={() => runAction(ev, 'resolve-dispute', { uphold: false, note: 'dispute dismissed' })} className="text-gray-600 hover:underline">Dismiss</button>
                              </>
                            ) : (
                              !['rejected', 'reversed'].includes(ev.status) && (
                                <button onClick={() => { setReasonFor({ id: ev.id, action: 'dispute' }); setReason(''); }} className="inline-flex items-center gap-1 text-gray-500 hover:text-amber-700"><Flag className="h-3 w-3" /> Hold</button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                      {reasonFor?.id === ev.id && needsReason(reasonFor.action) && (
                        <tr><td colSpan={7} className="pb-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <input id={`oc-reason-${ev.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="flex-1 min-w-[16rem] border rounded px-2 py-1.5 text-sm" />
                            <button
                              disabled={!reason.trim() || !!busy}
                              onClick={() => { const a = reasonFor.action; setReasonFor(null); runAction(ev, a, a === 'dispute' ? { note: reason } : { reason }); }}
                              className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                            >
                              {reasonFor.action === 'reject' ? 'Reject outcome' : reasonFor.action === 'reverse' ? 'Reverse and credit' : 'Hold off invoices'}
                            </button>
                            <button onClick={() => setReasonFor(null)} className="text-sm text-gray-500 hover:underline">Cancel</button>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
